// Coastline and ground-imagery data for Earth around the Kennedy launch site.
//
// Three nested levels, finest first (see scripts/fetch-ksc-imagery.mjs):
//   near    16 x 18 km around LC-39A, NAIP imagery 3.9 m/px, mask 7.8 m/px
//   far     1.25 deg box, NAIP imagery 33 m/px, mask 61 m/px
//   region  the Blue Marble tile's box (florida_region.webp), mask only
// Each mask texel holds two signed distances in metres (land positive): to any
// water, and to the open ocean alone. The CPU copy answers terrainHeight() and
// isWater(); the GPU copy (one RG8 array texture, a layer per level) drives the
// shoreline, water and beach shading. Levels cross-fade over a border band at
// their edges, on the CPU exactly as in the shader.
//
// Nothing loads at import, so the headless physics tests (Node) see a coast-free
// Earth; loadCoast() is called by the first Earth terrain patch in the browser.

import * as THREE from 'three';
import { fetchPng } from './pngdata.js';
import { KSC_LEVELS } from './kscLevels.js';
import { TEXTURE_BASE } from './textures.js';

export const COAST_ORDER = ['near', 'far', 'region'];
// Fraction of a level's box over which it fades into the next coarser one.
export const COAST_BORDER = { near: 0.05, far: 0.04, region: 0.004 };

const levels = COAST_ORDER.map((name, layer) => ({ name, layer, ...KSC_LEVELS[name], data: null }));
let loading = null;
let ready = false;
const listeners = new Set();
let globalOcean = null;   // { w, h, data } from the app's ocean mask, for isWater beyond the region

// Shared by every Earth terrain material. The per-patch uv transforms live on
// the materials themselves, since they depend on the patch centre.
export const coastUniforms = {
  uCoastOn: { value: 0 },
  uCoastMasks: { value: null },
  uCoastNearImg: { value: null },
  uCoastFarImg: { value: null },
  uCoastNearImgOn: { value: 0 },
  uCoastFarImgOn: { value: 0 },
  uCoastRange: { value: new THREE.Vector3(levels[0].range, levels[1].range, levels[2].range) },
  uCoastBorder: { value: new THREE.Vector3(COAST_BORDER.near, COAST_BORDER.far, COAST_BORDER.region) }
};

export function coastLevels() { return levels; }
export function coastReady() { return ready; }
export function onCoastReady(cb) {
  if (ready) { cb(); return () => {}; }
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function imageryTexture(name, flag) {
  const t = new THREE.TextureLoader().load(TEXTURE_BASE + name + '.webp', () => { flag.value = 1; });
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.anisotropy = 16;
  return t;
}

export function loadCoast() {
  if (loading) return loading;
  if (typeof window === 'undefined' || typeof fetch === 'undefined') return Promise.resolve(false);
  coastUniforms.uCoastNearImg.value = imageryTexture('ksc_near', coastUniforms.uCoastNearImgOn);
  coastUniforms.uCoastFarImg.value = imageryTexture('ksc_far', coastUniforms.uCoastFarImgOn);
  loading = (async () => {
    installCoast(await Promise.all(levels.map(L => fetchPng(TEXTURE_BASE + `ksc_${L.name}_mask.png`))));
    loadGlobalOcean();
    return true;
  })().catch((err) => { console.warn('[rocketry] coastline data failed to load; the ground stays coast-free', err); return false; });
  return loading;
}

// Takes decoded masks in COAST_ORDER (pngdata.js output). Exported so a Node
// script can install masks read from disk and check heights and isWater.
export function installCoast(masks) {
  const n = levels[0].maskSize;
  const all = new Uint8Array(n * n * 2 * levels.length);
  masks.forEach((m, i) => {
    if (m.width !== n || m.height !== n || m.channels !== 2) throw new Error(`ksc_${levels[i].name}_mask.png is ${m.width}x${m.height}x${m.channels}, expected ${n}x${n}x2`);
    levels[i].data = m.data;
    all.set(m.data, i * n * n * 2);
  });
  const tex = new THREE.DataArrayTexture(all, n, n, levels.length);
  tex.format = THREE.RGFormat;
  tex.type = THREE.UnsignedByteType;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  coastUniforms.uCoastMasks.value = tex;
  coastUniforms.uCoastOn.value = 1;
  ready = true;
  for (const cb of [...listeners]) { try { cb(); } catch (err) { console.error('[rocketry] coast listener failed', err); } }
  listeners.clear();
}

// The app's own ocean mask (2048 x 1024, about 20 km/px), read back once for
// splashdown tests anywhere outside the region tile.
function loadGlobalOcean() {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const c = document.createElement('canvas');
      c.width = img.width; c.height = img.height;
      const x = c.getContext('2d', { willReadFrequently: true });
      x.drawImage(img, 0, 0);
      const px = x.getImageData(0, 0, c.width, c.height).data;
      const data = new Uint8Array(c.width * c.height);
      for (let i = 0; i < data.length; i++) data[i] = px[i * 4];
      globalOcean = { w: c.width, h: c.height, data };
    } catch (err) { console.warn('[rocketry] global ocean mask unavailable', err); }
  };
  img.src = '/textures/earth_specular.jpg?v=ocean-mask-3';
}

const decodeCode = (c) => { const s = (c - 128) / 127; return s < 0 ? -s * s : s * s; };
function smoothstep(a, b, x) { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); }

// Bilinear sample of one level; returns its border weight (0 outside).
function sampleLevel(L, latDeg, lonDeg, out) {
  const u = (lonDeg - L.lonMin) / (L.lonMax - L.lonMin);
  const v = (latDeg - L.latMin) / (L.latMax - L.latMin);
  if (!(u > 0 && u < 1 && v > 0 && v < 1)) return 0;
  const n = L.maskSize, data = L.data;
  // Mask rows run north to south.
  const fx = u * n - 0.5, fy = (1 - v) * n - 0.5;
  const x0 = Math.max(0, Math.min(n - 2, Math.floor(fx))), y0 = Math.max(0, Math.min(n - 2, Math.floor(fy)));
  const tx = Math.min(1, Math.max(0, fx - x0)), ty = Math.min(1, Math.max(0, fy - y0));
  const i00 = (y0 * n + x0) * 2, i10 = i00 + 2, i01 = i00 + n * 2, i11 = i01 + 2;
  const lerp2 = (k) => (data[i00 + k] * (1 - tx) + data[i10 + k] * tx) * (1 - ty) + (data[i01 + k] * (1 - tx) + data[i11 + k] * tx) * ty;
  out.d = decodeCode(lerp2(0)) * L.range;
  out.ocean = decodeCode(lerp2(1)) * L.range;
  const b = COAST_BORDER[L.name];
  return smoothstep(0, b, Math.min(u, 1 - u, v, 1 - v));
}

const _s = { d: 0, ocean: 0 };
// Signed distances (metres, land positive) to any water and to the open
// ocean at a geodetic position in degrees, or null where no level covers it
// (or before the data has loaded).
export function coastDistance(latDeg, lonDeg, out = { d: 0, ocean: 0 }) {
  if (!ready) return null;
  let have = false;
  for (let i = levels.length - 1; i >= 0; i--) {
    const w = sampleLevel(levels[i], latDeg, lonDeg, _s);
    if (w <= 0) continue;
    if (!have) { out.d = _s.d; out.ocean = _s.ocean; have = true; continue; }
    out.d += (_s.d - out.d) * w;
    out.ocean += (_s.ocean - out.ocean) * w;
  }
  return have ? out : null;
}

// Ocean anywhere on Earth from the app's mask, or null if it is not loaded.
export function globalOceanAt(latDeg, lonDeg) {
  if (!globalOcean) return null;
  const { w, h, data } = globalOcean;
  const x = Math.floor(((lonDeg + 180) / 360) * w) % w;
  const y = Math.min(h - 1, Math.max(0, Math.floor(((90 - latDeg) / 180) * h)));
  return data[y * w + ((x + w) % w)] > 127;
}
